import { useEffect, useRef, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from "@mui/material";
import api from "../api/axios";
import { useBusinessContext } from "../BusinessContext";
import { personScopeUrl } from "./personScope";
import { personDisplayName } from "./personName";
import DialogDateField from "./DialogDateField";
import LoadingIndicator from './LoadingIndicator';
import LoadError from './LoadError';
import { normalizeDialogDate } from "./dialogDate";

const defaults = () => ({ Eintrittsdatum: "", Austrittsdatum: "", Bereich: "", Status: "aktiv", Aktiv: true });
const assignmentForm = record => ({
  Eintrittsdatum: record.Eintrittsdatum?.slice(0, 10) || "",
  Austrittsdatum: record.Austrittsdatum?.slice(0, 10) || "",
  Bereich: record.Bereich || "", Status: record.Status || "aktiv", Aktiv: Boolean(record.Aktiv),
});

export default function RoleAssignments({ role, person, onClose, onSaved }) {
  const context = useBusinessContext();
  const { mandantId, betriebId, selectedMandant, selectedBetrieb } = context;
  const [initialScope] = useState({ mandantId, betriebId });
  const [invalidated, setInvalidated] = useState(false);
  const changed = mandantId !== initialScope.mandantId || betriebId !== initialScope.betriebId;
  const incomplete = !mandantId || !betriebId || !selectedMandant || !selectedBetrieb;
  const stale = changed || invalidated;
  const [people, setPeople] = useState(person ? [person] : []);
  const [chosen, setChosen] = useState(String(person?.Id || ""));
  const selectedPerson = person || people.find(item => String(item.Id) === chosen);
  const foreignPerson = selectedPerson?.Mandant_Id != null && Number(selectedPerson.Mandant_Id) !== Number(mandantId);
  const [records, setRecords] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(defaults);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const active = useRef(true);
  const latestScope = useRef({ mandantId, betriebId });
  latestScope.current = { mandantId, betriebId };
  const hunter = role === "jaeger";
  const path = `/personen/${hunter ? "jaeger" : "kunden"}zuordnungen`;

  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => { if (changed) setInvalidated(true); }, [changed]);
  useEffect(() => {
    let cancelled = false;
    if (incomplete || stale || foreignPerson) { setLoading(false); return; }
    setLoading(true); setLoadFailed(false); setError(""); setRecords([]); setEdit(null); setForm(defaults());
    const load = async () => {
      try {
        if (!person) {
          const response = await api.get(personScopeUrl("/personen", mandantId));
          if (!cancelled) setPeople(response.data);
        }
        if (chosen) {
          const response = await api.get(personScopeUrl(`${path}?person_id=${chosen}`, mandantId, betriebId));
          if (!cancelled) {
            const rows = response.data;
            setRecords(rows);
            // Existing history stays selectable; never silently insert a second assignment.
            const latest = [...rows].sort((a, b) =>
              (a.Eintrittsdatum || "").localeCompare(b.Eintrittsdatum || "") || a.Id - b.Id).pop();
            if (latest) { setEdit(latest.Id); setForm(assignmentForm(latest)); }
          }
        }
      } catch (e) {
        if (!cancelled) { setLoadFailed(true); setError(e.response?.data?.error || "Zuordnungen konnten nicht geladen werden."); }
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [mandantId, betriebId, chosen, path, person, incomplete, stale, foreignPerson, attempt]);

  const dateError = hunter && form.Eintrittsdatum && form.Austrittsdatum && normalizeDialogDate(form.Austrittsdatum) < normalizeDialogDate(form.Eintrittsdatum);
  const areaError = hunter && form.Bereich.length > 100;
  const organizationHunter = hunter && selectedPerson?.Typ === "ORGANIZATION";
  const disabled = organizationHunter || busy || loading || loadFailed || incomplete || stale || foreignPerson || !selectedPerson;
  const update = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const save = async () => {
    if (disabled || submitting.current || dateError || areaError) return;
    submitting.current = true; setBusy(true); setError("");
    try {
      const data = { mandant_id: mandantId, betrieb_id: betriebId, Person_Id: Number(chosen),
        ...(hunter ? { Eintrittsdatum: normalizeDialogDate(form.Eintrittsdatum) || null, Austrittsdatum: normalizeDialogDate(form.Austrittsdatum) || null,
          Bereich: form.Bereich || null, Status: form.Status } : { Aktiv: form.Aktiv }) };
      if (edit !== null) await api.put(`${path}/${edit}`, data); else await api.post(path, data);
      if (active.current && latestScope.current.mandantId === mandantId && latestScope.current.betriebId === betriebId) onSaved();
    } catch (e) {
      if (active.current) setError(e.response?.data?.error || "Zuordnung konnte nicht gespeichert werden.");
    } finally {
      submitting.current = false;
      if (active.current) setBusy(false);
    }
  };

  return <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{hunter ? "Jägerzuordnung bearbeiten" : "Kundenzuordnung bearbeiten"}</DialogTitle>
    <DialogContent><Stack spacing={1} sx={{ pt: 1 }}>
      {incomplete && <Alert severity="warning">Bitte Mandant und Betrieb im Geschäftskontext auswählen.</Alert>}
      {stale && <Alert severity="warning">Der Geschäftskontext wurde geändert. Bitte den Dialog schließen und erneut öffnen.</Alert>}
      {foreignPerson && <Alert severity="error">Die Person gehört nicht zum ausgewählten Mandanten.</Alert>}
      {organizationHunter && <Alert severity="error">Organisationen können nicht als Jäger zugeordnet werden.</Alert>}
      {error && (loadFailed ? <LoadError onRetry={() => setAttempt(value => value + 1)}>{error}</LoadError> : <Alert severity="error">{error}</Alert>)}
      {loading && <LoadingIndicator label="Zuordnung wird geladen …" />}
      {!person && <TextField size="small" select label="Person zuordnen" value={chosen} disabled={busy || stale || incomplete}
        onChange={e => setChosen(e.target.value)}>
        {people.filter(item => !hunter || item.Typ !== "ORGANIZATION").map(item => <MenuItem key={item.Id} value={String(item.Id)}>{personDisplayName(item)}</MenuItem>)}
      </TextField>}
      <Typography variant="body2">Personen-ID: {selectedPerson?.Id ?? "–"}</Typography>
      {selectedPerson?.Typ === "ORGANIZATION" && <Typography variant="body2">Organisation: {selectedPerson.Organisationsname}</Typography>}
      <Typography variant="body2">Vorname: {selectedPerson?.Vorname || "–"}</Typography>
      <Typography variant="body2">Nachname: {selectedPerson?.Name || "–"}</Typography>
      {records.length > 1 && <TextField size="small" select label="Bestehende Zuordnung" value={edit || ""} disabled={disabled}
        onChange={e => { const record = records.find(item => item.Id === Number(e.target.value)); setEdit(record.Id); setForm(assignmentForm(record)); }}>
        {records.map(record => <MenuItem key={record.Id} value={record.Id}>{record.Status}: {record.Eintrittsdatum || "unbekannt"} – {record.Austrittsdatum || "offen"}</MenuItem>)}
      </TextField>}
      {hunter ? <>
        <TextField size="small" select label="Jägerstatus" value={form.Status} disabled={disabled} onChange={e => update("Status", e.target.value)}>
          {["aktiv", "inaktiv", "spezial"].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
        </TextField>
        <DialogDateField size="small" label="Eintrittsdatum" value={form.Eintrittsdatum} disabled={disabled}
          slotProps={{ inputLabel: { shrink: true } }} onChange={e => update("Eintrittsdatum", e.target.value)} />
        <DialogDateField size="small" label="Austrittsdatum" value={form.Austrittsdatum} disabled={disabled} error={Boolean(dateError)}
          helperText={dateError ? "Austritt liegt vor Eintritt." : ""} slotProps={{ inputLabel: { shrink: true } }} onChange={e => update("Austrittsdatum", e.target.value)} />
        <TextField size="small" label="Bereich" value={form.Bereich} disabled={disabled} error={areaError}
          helperText={areaError ? "Bereich darf maximal 100 Zeichen enthalten." : ""}
          slotProps={{ htmlInput: { maxLength: 100 } }} onChange={e => update("Bereich", e.target.value)} />
      </> : <TextField size="small" select label="Kundenstatus" value={form.Aktiv ? "aktiv" : "inaktiv"} disabled={disabled}
        onChange={e => update("Aktiv", e.target.value === "aktiv")}>
        <MenuItem value="aktiv">aktiv</MenuItem><MenuItem value="inaktiv">inaktiv</MenuItem>
      </TextField>}
    </Stack></DialogContent>
    <DialogActions>
      <Button disabled={busy} onClick={onClose}>Abbrechen</Button>
      <Button variant="contained" disabled={disabled || Boolean(dateError) || areaError} onClick={save}>Speichern</Button>
    </DialogActions>
  </Dialog>;
}
